import * as React from 'react';
import type { MouseEvent } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Box, StyleSheet, breakPoint, color, css } from '@coursera/coursera-ui';
import type { RootClassNameProp } from '@coursera/coursera-ui';

import GoogleLogoSVG from 'bundles/enterprise-admin-grow-with-google/components/svgs/GoogleLogoSVG';
import { Button, CopyToClipboardButton } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = StyleSheet.create({
  container: {
    minHeight: '227px',
    background: color.primary,
    display: 'flex',
    border: `1px solid ${color.divider}`,
    borderRadius: '0 3px 3px 0',
    overflow: 'hidden',
    ':hover': {
      boxShadow: '0 2px 10px 0 rgba(0,0,0,0.2)',
    },
    [`@media (max-width: ${breakPoint.md}px)`]: {
      flexDirection: 'column',
    },
  },
  summary: {
    flexBasis: 0,
    flexGrow: 1,
    padding: '24px 36px',
    color: color.white,
  },
  programCreationTimeStamp: {
    color: color.white,
    fontSize: '12px',
    letterSpacing: 0,
    lineHeight: '18px',
    marginBottom: '4px',
    minHeight: '18px',
  },
  programName: {
    color: color.white,
    fontSize: '32px',
    fontWeight: 'bold',
    lineHeight: '44px',
    wordBreak: 'break-word',
    hyphens: 'auto',
  },
  programIdBox: {
    height: '36px',
    border: `1px solid ${color.divider}`,
    borderRadius: '4px',
    color: color.primary,
    fontSize: '12px',
    paddingLeft: '16px',
    marginTop: '24px',
  },
  inviteLearners: {
    flexBasis: 0,
    flexGrow: 1,
    textAlign: 'center',
    display: 'block',
    backgroundColor: 'white',
    padding: '24px 48px',
  },
  programTitle: {
    fontSize: '16px',
    textAlign: 'center',
    fontWeight: 'bold',
    color: color.primaryText,
    marginTop: '32px',
  },
  gwgProgramTitle: {
    fontSize: '16px',
    textAlign: 'center',
    fontWeight: 'bold',
    color: color.primaryText,
    marginTop: '8px',
  },
  programDescription: {
    fontSize: '16px',
    textAlign: 'center',
    color: color.secondaryText,
    marginBottom: '16px',
  },
});

type Props = {
  thirdPartyOrganizationSlug: string;
  onContractsClick?: (event: MouseEvent) => void;
  programId: string;
  programCreatedAt?: number;
  programName: string;
  programSlug: string;
  showProgramId?: boolean;
  rootClassName?: RootClassNameProp;
  onContinueProgramSetup: ({
    programSlug,
    thirdPartyOrganizationSlug,
  }: {
    programSlug: string;
    thirdPartyOrganizationSlug: string;
  }) => void;
  isGwG?: boolean;
};

const ProgramSetupCard: React.FC<Props> = ({
  thirdPartyOrganizationSlug,
  programId,
  programCreatedAt,
  programName,
  programSlug,
  showProgramId,
  rootClassName,
  onContinueProgramSetup,
  isGwG,
}) => {
  const handleOnContinueProgramSetUp = (event?: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    event?.stopPropagation();
    event?.preventDefault();
    onContinueProgramSetup({ programSlug, thirdPartyOrganizationSlug });
  };

  return (
    <div {...css(styles.container, rootClassName)} data-e2e={`Program_Summary~${programId}`}>
      <Box rootClassName={styles.summary} flexDirection="column" justifyContent="between">
        <Box flexDirection="column" justifyContent="start">
          <div {...css(styles.programCreationTimeStamp)}>
            {programCreatedAt && (
              <FormattedMessage
                message={_t('Program created on {date}')}
                date={formatDateTimeDisplay(programCreatedAt, 'LL')}
              />
            )}
          </div>
          <div {...css(styles.programName)}>{programName}</div>
        </Box>
      </Box>
      {isGwG ? (
        <div {...css(styles.inviteLearners)} data-test-id="continue-program-setup">
          <GoogleLogoSVG />
          <p {...css(styles.gwgProgramTitle)}>{_t('Prepare learners for in demand entry-level digital jobs.')}</p>
          <p {...css(styles.programDescription)}>{_t('Enable learners to earn a Google Professional Certificate.')}</p>
          <Button
            variant="primary"
            size="small"
            onClick={handleOnContinueProgramSetUp}
            data-e2e="continue-program-setup"
          >
            {_t('Continue program set up')}
          </Button>
          {showProgramId && (
            <Box justifyContent="between" rootClassName={styles.programIdBox} alignItems="center">
              <div>{_t('ID: #{id}', { id: programId })}</div>
              <CopyToClipboardButton textToCopy={programId}>{_t('Copy Program ID')}</CopyToClipboardButton>
            </Box>
          )}
        </div>
      ) : (
        <div {...css(styles.inviteLearners)} data-test-id="continue-program-setup">
          <p {...css(styles.programTitle)}>{_t('Get students learning today')}</p>
          <p {...css(styles.programDescription)}>
            {_t('Continue setting up your learning program in a few quick steps')}
          </p>
          <Button
            variant="primary"
            size="small"
            onClick={handleOnContinueProgramSetUp}
            data-e2e="continue-program-setup"
          >
            {_t('Continue program set up')}
          </Button>
          {showProgramId && (
            <Box justifyContent="between" rootClassName={styles.programIdBox} alignItems="center">
              <div>{_t('ID: #{id}', { id: programId })}</div>
              <CopyToClipboardButton size="small" textToCopy={programId}>
                {_t('Copy Program ID')}
              </CopyToClipboardButton>
            </Box>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramSetupCard;
